import { useCallback, useEffect, useState } from "react";

import { editorDraftStore, type EditorDraftBlob } from "./editorDraftStore";
import type { PersistedSnapshot } from "./types";

// Getting a device draft back into the editor (#336), in two moments:
//  - BEFORE mount (useDraftResume): the one read, so the editor seeds from
//    it synchronously. A draft of a NEW program is not resumed in silence
//    (Antenore, 10/10): «+ New program» asks «Resume» or «Start over», with
//    the draft's date. Start over throws the draft away.
//  - AFTER mount (useResumeConflict): a draft of an EXISTING program resumes
//    in silence, unless the server's content is no longer the draft's
//    baseline (a coach, Claude, another device wrote it since). Then: my
//    draft, or the server's. Compared on content (draftContent.ts).
//    S5: useProgramVersion asks the same question (`show`) when the open
//    read finds the server moved under edits not yet saved.
// Neither answer writes to the server.

export type StoredEditorDraft = { blob: EditorDraftBlob; savedAtMs: number | null };

export function useDraftResume(readKey: string | null, isNew: boolean) {
  const [read, setRead] = useState<{ stored: StoredEditorDraft | null } | null>(
    readKey === null ? { stored: null } : null,
  );
  const [answer, setAnswer] = useState<"resume" | "fresh" | null>(null);
  useEffect(() => {
    if (readKey === null) return;
    let live = true;
    void editorDraftStore.read(readKey).then((stored) => {
      if (live) setRead({ stored });
    });
    return () => {
      live = false;
    };
    // Once per mount: a later key change is the editor's own (create → edit).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const found = read?.stored ?? null;
  const asking = isNew && found !== null && answer === null;
  return {
    ready: read !== null && !asking,
    stored: answer === "fresh" ? null : found,
    newDraft: asking && found
      ? {
          savedAtMs: found.savedAtMs,
          onResume: () => setAnswer("resume"),
          onStartOver: () => {
            if (readKey !== null) void editorDraftStore.clear(readKey);
            setAnswer("fresh");
          },
        }
      : null,
  };
}

type ConflictState =
  | { phase: "settled" }
  | { phase: "checking" }
  | { phase: "open"; server: PersistedSnapshot };

export function useResumeConflict({
  stored,
  server,
  readServer,
  sameContent,
  resetTo,
  markSaved,
  currentBaseline,
}: {
  stored: StoredEditorDraft | null;
  /** The server's copy the host opened the editor with, normalised like the
   *  baseline (snapshotOf), or null when the host gave none. Read once. */
  server: (() => PersistedSnapshot) | null;
  /** Reads the server's copy by id, normalised; null when there is nothing
   *  readable under it. Used for a draft that carries a written id and came
   *  without a host copy (a create that went out, or was queued). */
  readServer: ((id: string) => Promise<PersistedSnapshot | null>) | null;
  sameContent: (a: PersistedSnapshot, b: PersistedSnapshot) => boolean;
  resetTo: (value: PersistedSnapshot) => void;
  markSaved: (baseline: PersistedSnapshot) => void;
  /** The editor's baseline NOW: a Save made while the read was out moves
   *  it to what the server then holds, which is no news to ask about. */
  currentBaseline?: () => PersistedSnapshot | undefined;
}) {
  const [state, setState] = useState<ConflictState>(() => {
    if (stored === null) return { phase: "settled" };
    if (server !== null) {
      const copy = server();
      return sameContent(stored.blob.baseline, copy)
        ? { phase: "settled" }
        : { phase: "open", server: copy };
    }
    return stored.blob.serverId !== null && readServer !== null
      ? { phase: "checking" }
      : { phase: "settled" };
  });
  // The read happens once, at mount. Not found (a create the server never
  // confirmed), unreadable, or offline: nothing to compare, the draft resumes.
  useEffect(() => {
    const id = stored?.blob.serverId ?? null;
    if (state.phase !== "checking" || id === null || readServer === null) return;
    let live = true;
    const baseline = stored!.blob.baseline;
    readServer(id)
      .then((snap) => {
        if (!live) return;
        const now = currentBaseline?.();
        const known =
          snap === null ||
          sameContent(baseline, snap) ||
          (now !== undefined && sameContent(now, snap));
        setState(known ? { phase: "settled" } : { phase: "open", server: snap });
      })
      .catch(() => {
        if (live) setState({ phase: "settled" });
      });
    return () => {
      live = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // Either way the baseline becomes the server's copy: kept, the draft reads
  // as unsaved against it; taken, the editor matches it.
  const resolve = useCallback(
    (keep: boolean) => {
      if (state.phase !== "open") return;
      if (!keep) resetTo(state.server);
      markSaved(state.server);
      setState({ phase: "settled" });
    },
    [state, resetTo, markSaved],
  );
  // Asks about `server` now, or swaps in this fresher copy if already asking.
  const show = useCallback((copy: PersistedSnapshot) => {
    setState({ phase: "open", server: copy });
  }, []);
  return {
    show,
    open: state.phase === "open",
    /** True until the resume is settled: the device draft is left as it is. */
    pending: state.phase !== "settled",
    onKeepDraft: () => resolve(true),
    onTakeServer: () => resolve(false),
  };
}
